import React, { useEffect, useMemo, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import ModulePanel from '../../../ui/ModulePanel/ModulePanel'
import Field from '../../../ui/Field/Field'
import Switch from '../../../ui/Switch/Switch'
import Button from '../../../ui/Button/Button'
import Spinner from '../../../ui/Spinner/Spinner'
import StatusMessage, { useStatusMessage } from '../../../ui/StatusMessage/StatusMessage'
import getDoc from '../../../utils/db/getDoc'
import getCollection from '../../../utils/db/getCollection'
import loadSelectableBrands from '../../../api/marketing/loadSelectableBrands'
import loadRelatedProductCatalog from '../../../api/marketing/loadRelatedProductCatalog'
import buildRelatedProductSearchFilters from '../../../api/marketing/buildRelatedProductSearchFilters'
import useLazyFirestoreCatalog from '../UtmBuilder/useLazyFirestoreCatalog'
import { CATALOG_TAXONOMY_COLLECTION } from '../../Products/components/CatalogTaxonomy/catalogTaxonomyConstants'
import { createPackagingCategorySelection } from '../../../api/marketing/gifts/packagingCategorySelection'
import {
  buildCatalogTaxonomyNodePathLabel,
  buildCatalogTaxonomyNodePathSegments,
  buildCatalogTaxonomyNodesById,
} from '../../Products/productSchema/formatProductTaxonomySummary'
import upsertAmeCampaign from '../../../api/marketing/ameCampaigns/upsertAmeCampaign'
import { buildPeriodPayload, timestampToKyivFields } from '../../../api/marketing/ameCampaigns/kyivDateTimeFields'
import {
  AME_CAMPAIGN_SCOPE,
  AME_INCLUDE_MODE,
  AME_PROMOTION_TYPE,
  AME_PROMOTION_TYPE_OPTIONS,
  AME_PROMO_CODE_TYPE,
  AME_PROMO_CODE_TYPE_OPTIONS,
  AME_DISCOUNT_KIND,
  AME_DISCOUNT_KIND_OPTIONS,
  AME_ISSUE_KIND,
  AME_ISSUE_KIND_BY_PHRASE,
  AME_ISSUE_KIND_OPTIONS,
  DEFAULT_AME_CAMPAIGN_PERCENT,
  PROMO_CAMPAIGNS_COLLECTION,
} from '../../../api/marketing/ameCampaigns/ameCampaignConstants'
import AmeCampaignAssortmentFields from './AmeCampaignAssortmentFields'
import giftStyles from '../Gifts/GiftsFund.module.scss'
import styles from './AmeCampaigns.module.scss'
import '../UtmBuilder/UtmBuilder.sass'

const todayKyivUi = () => {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Kyiv',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).formatToParts(new Date())
  const get = (type) => parts.find((part) => part.type === type)?.value
  return `${get('day')}.${get('month')}.${get('year')}`
}

const blankDraft = () => ({
  title: '',
  issueKind: AME_ISSUE_KIND,
  phrase: '',
  isActive: false,
  discountKind: AME_DISCOUNT_KIND.percent,
  percent: String(DEFAULT_AME_CAMPAIGN_PERCENT),
  amount: '',
  applyToSpecPrice: false,
  openEnded: true,
  startDate: todayKyivUi(),
  startTime: '00:00',
  endDate: '',
  endTime: '23:59',
  scope: AME_CAMPAIGN_SCOPE.premium,
  includeMode: AME_INCLUDE_MODE.allInScope,
  includeBrands: [],
  includeCategories: [],
  includeProducts: [],
  excludeBrands: [],
  excludeCategories: [],
  excludeProducts: [],
  description: '',
})

const selectionsFromNodeIds = (nodeIds, nodes) => {
  const nodesById = buildCatalogTaxonomyNodesById(nodes || [])
  return (nodeIds || [])
    .map((rawId) => {
      const id = String(rawId)
      const node = nodesById[id] || nodesById[rawId]
      if (!node) return null
      return createPackagingCategorySelection({
        node,
        pathLabel: buildCatalogTaxonomyNodePathLabel(id, nodesById),
        pathIds: buildCatalogTaxonomyNodePathSegments(id, nodesById).map((segment) => segment.id),
      })
    })
    .filter(Boolean)
}

function AmeCampaignEditor() {
  const { id } = useParams()
  const history = useHistory()
  const isNew = !id
  const [statusMessage, setStatusMessage] = useStatusMessage()
  const [loadStatus, setLoadStatus] = useState(isNew ? 'ready' : 'loading')
  const [isSaving, setIsSaving] = useState(false)
  const [draft, setDraft] = useState(blankDraft)
  const [descriptionSeed, setDescriptionSeed] = useState('')
  const [taxonomyNodes, setTaxonomyNodes] = useState([])

  const { data: brands, isLoading: brandsLoading, loadError: brandsError, load: loadBrands } = useLazyFirestoreCatalog({
    loadCatalog: loadSelectableBrands,
    errorMessage: 'Не вдалося завантажити бренди',
    logLabel: '[AmeCampaignEditor] brands',
  })

  const {
    data: searchIndex,
    isLoading: productsLoading,
    loadError: productsError,
    load: loadProducts,
  } = useLazyFirestoreCatalog({
    loadCatalog: loadRelatedProductCatalog,
    errorMessage: 'Не вдалося завантажити товари',
    logLabel: '[AmeCampaignEditor] products',
  })

  useEffect(() => {
    loadBrands()
    getCollection({ path: CATALOG_TAXONOMY_COLLECTION, docIdName: 'id' })
      .then((nodes) => setTaxonomyNodes(Array.isArray(nodes) ? nodes : []))
      .catch((error) => {
        console.error('[AmeCampaignEditor] taxonomy', error)
      })
  }, [loadBrands])

  useEffect(() => {
    if (isNew) setDescriptionSeed('')
  }, [isNew, id])

  useEffect(() => {
    if (isNew) return undefined
    let cancelled = false
    setLoadStatus('loading')
    getDoc({ path: PROMO_CAMPAIGNS_COLLECTION, docId: id, docIdName: 'id' })
      .then((doc) => {
        if (cancelled) return
        if (!doc) {
          setLoadStatus('missing')
          return
        }
        const start = timestampToKyivFields(doc.startsAt, '00:00')
        const end = timestampToKyivFields(doc.endsAt, '23:59')
        const description = typeof doc.description === 'string' ? doc.description : ''
        setDescriptionSeed(description)
        setDraft({
          title: doc.title || '',
          issueKind: doc.issue?.kind || AME_ISSUE_KIND,
          phrase: doc.issue?.phrase || '',
          isActive: doc.isActive === true || (doc.isActive == null && doc.status === 'published'),
          discountKind: doc.benefit?.kind === 'fixedAmount' ? AME_DISCOUNT_KIND.fixed : AME_DISCOUNT_KIND.percent,
          percent: String(doc.benefit?.percent ?? DEFAULT_AME_CAMPAIGN_PERCENT),
          amount: doc.benefit?.amount != null ? String(doc.benefit.amount) : '',
          applyToSpecPrice: doc.applyToSpecPrice === true,
          openEnded: doc.endsAt == null,
          startDate: start.date,
          startTime: start.time,
          endDate: doc.endsAt == null ? '' : end.date,
          endTime: doc.endsAt == null ? '23:59' : end.time,
          scope: doc.scope || AME_CAMPAIGN_SCOPE.premium,
          includeMode: doc.include?.mode || AME_INCLUDE_MODE.allInScope,
          includeBrands: [],
          includeCategories: [],
          includeProducts: Array.isArray(doc.include?.productRefs) ? doc.include.productRefs : [],
          excludeBrands: [],
          excludeCategories: [],
          excludeProducts: Array.isArray(doc.exclude?.productRefs) ? doc.exclude.productRefs : [],
          includeBrandIds: doc.include?.brandIds || [],
          excludeBrandIds: doc.exclude?.brandIds || [],
          includeCategoryIds: doc.include?.categoryNodeIds || [],
          excludeCategoryIds: doc.exclude?.categoryNodeIds || [],
          description,
        })
        setLoadStatus('ready')
      })
      .catch((error) => {
        console.error('[AmeCampaignEditor] load', error)
        if (!cancelled) setLoadStatus('error')
      })
    return () => {
      cancelled = true
    }
  }, [id, isNew])

  useEffect(() => {
    if (!brands || brands.length === 0) return
    setDraft((prev) => {
      const mapIds = (ids, current) => {
        if (Array.isArray(current) && current.length > 0) return current
        return (ids || [])
          .map((brandId) => brands.find((brand) => String(brand.id) === String(brandId)))
          .filter(Boolean)
      }
      const includeBrands = mapIds(prev.includeBrandIds, prev.includeBrands)
      const excludeBrands = mapIds(prev.excludeBrandIds, prev.excludeBrands)
      const includeSame =
        includeBrands.length === (prev.includeBrands || []).length &&
        includeBrands.every((brand, index) => brand?.id === prev.includeBrands[index]?.id)
      const excludeSame =
        excludeBrands.length === (prev.excludeBrands || []).length &&
        excludeBrands.every((brand, index) => brand?.id === prev.excludeBrands[index]?.id)
      if (includeSame && excludeSame) return prev
      return {
        ...prev,
        includeBrands,
        excludeBrands,
      }
    })
  }, [brands, draft.includeBrandIds, draft.excludeBrandIds])

  useEffect(() => {
    if (!taxonomyNodes.length) return
    setDraft((prev) => {
      const includeCategories =
        prev.includeCategories.length > 0
          ? prev.includeCategories
          : selectionsFromNodeIds(prev.includeCategoryIds, taxonomyNodes)
      const excludeCategories =
        prev.excludeCategories.length > 0
          ? prev.excludeCategories
          : selectionsFromNodeIds(prev.excludeCategoryIds, taxonomyNodes)
      if (includeCategories === prev.includeCategories && excludeCategories === prev.excludeCategories) {
        return prev
      }
      return {
        ...prev,
        includeCategories,
        excludeCategories,
      }
    })
  }, [taxonomyNodes, draft.includeCategoryIds, draft.excludeCategoryIds])

  useEffect(() => {
    if (!searchIndex || searchIndex.length === 0) return
    const enrich = (refs) =>
      (refs || []).map((ref) => {
        if (ref.name) return ref
        const found = searchIndex.find((product) => product.id === ref.productId && product.source === ref.catalog)
        return found ? { ...ref, name: found.name } : ref
      })
    setDraft((prev) => ({
      ...prev,
      includeProducts: enrich(prev.includeProducts),
      excludeProducts: enrich(prev.excludeProducts),
    }))
  }, [searchIndex])

  const productFilters = useMemo(() => buildRelatedProductSearchFilters([]), [])

  const patch = (next) => setDraft((prev) => ({ ...prev, ...next }))

  const parseUiNumber = (raw) => {
    const source = String(raw ?? '')
      .trim()
      .replace(/\s/g, '')
      .replace(',', '.')
    if (!source) return NaN
    return Number(source)
  }

  const buildPayload = () => {
    const title = String(draft.title || '').trim()
    if (!title) {
      return { ok: false, message: 'Вкажіть назву акції' }
    }

    const period = buildPeriodPayload({
      startDate: draft.startDate,
      startTime: draft.startTime,
      endDate: draft.endDate,
      endTime: draft.endTime,
      openEnded: draft.openEnded,
    })
    if (!period.ok) {
      return { ok: false, message: period.error }
    }

    const isFixed = draft.discountKind === AME_DISCOUNT_KIND.fixed
    let percent
    let amount
    if (isFixed) {
      amount = parseUiNumber(draft.amount)
      if (!Number.isFinite(amount) || amount <= 0) {
        return { ok: false, message: 'Вкажіть суму знижки' }
      }
    } else {
      percent = parseUiNumber(draft.percent)
      if (!Number.isInteger(percent) || percent < 1 || percent > 100) {
        return { ok: false, message: 'Відсоток знижки має бути цілим числом від 1 до 100' }
      }
    }

    const payload = {
      title,
      issueKind: draft.issueKind,
      ...(draft.issueKind === AME_ISSUE_KIND_BY_PHRASE ? { phrase: draft.phrase } : {}),
      isActive: draft.isActive === true,
      discountKind: draft.discountKind,
      applyToSpecPrice: draft.applyToSpecPrice === true,
      description: typeof draft.description === 'string' ? draft.description : '',
      startsAt: period.startsAt,
      endsAt: period.endsAt,
      openEnded: period.openEnded,
      scope: draft.scope,
      include: {
        mode: draft.includeMode,
        brandIds: draft.includeBrands.map((brand) => brand.id),
        categoryNodeIds: draft.includeCategories.map((item) => item.catalogTaxonomyNodeId).filter(Boolean),
        productRefs: draft.includeProducts.map((item) => ({
          catalog: item.catalog,
          productId: item.productId,
        })),
      },
      exclude: {
        brandIds: draft.excludeBrands.map((brand) => brand.id),
        categoryNodeIds: draft.excludeCategories.map((item) => item.catalogTaxonomyNodeId).filter(Boolean),
        productRefs: draft.excludeProducts.map((item) => ({
          catalog: item.catalog,
          productId: item.productId,
        })),
      },
    }

    if (!isNew) payload.campaignId = id
    if (isFixed) payload.amount = amount
    else payload.percent = percent

    return { ok: true, payload }
  }

  const handleSave = async () => {
    if (isSaving) return
    const built = buildPayload()
    if (!built.ok) {
      setStatusMessage({ show: true, type: 'fail', message: built.message, closeAfter: 6000 })
      return
    }
    setIsSaving(true)
    try {
      const result = await upsertAmeCampaign(built.payload)
      if (!result.ok) {
        setStatusMessage({ show: true, type: 'fail', message: result.message, closeAfter: 6000 })
        return
      }
      history.push({
        pathname: '/marketing/ame',
        state: { justSaved: true },
      })
    } catch (error) {
      console.error('[AmeCampaignEditor] save', error)
      setStatusMessage({
        show: true,
        type: 'fail',
        message: error?.message || 'Не вдалося зберегти акцію',
        closeAfter: 6000,
      })
    } finally {
      setIsSaving(false)
    }
  }

  if (loadStatus === 'loading') {
    return <Spinner type="module" />
  }

  if (loadStatus === 'missing' || loadStatus === 'error') {
    return (
      <ModulePanel className={styles.panel} title="Акції Ame">
        <p className={giftStyles.error}>Не вдалося завантажити акцію</p>
        <Button
          title="До реєстру"
          theme="solid"
          fill="normal"
          size={32}
          onClick={() => history.push('/marketing/ame')}
        />
      </ModulePanel>
    )
  }

  return (
    <div className={giftStyles.editorRoot}>
      {statusMessage.show ? (
        <StatusMessage className="Site-StatusMessage" type={statusMessage.type} message={statusMessage.message} />
      ) : null}
      <ModulePanel className={styles.panel} title={isNew ? 'Нова акція Ame' : draft.title || 'Акція Ame'}>
        <div className={styles.fields}>
          <div className={giftStyles.switchRow}>
            <Switch
              checked={draft.isActive === true}
              onChange={(next) => patch({ isActive: next })}
              ariaLabel="Активна"
            />
            <span className={giftStyles.switchLabel}>Активна</span>
          </div>
          <div className={styles.periodRow}>
            <div className={styles.titleField}>
              <Field
                fieldType="input"
                config={{ label: 'Назва', type: 'text' }}
                value={draft.title}
                changed={(event) => patch({ title: event.target.value })}
              />
            </div>
            <div className={styles.periodRowField}>
              <Field
                fieldType="select"
                config={{
                  label: 'Тип акції',
                  getOptions: AME_PROMOTION_TYPE_OPTIONS,
                }}
                value={AME_PROMOTION_TYPE}
                changed={() => {}}
              />
            </div>
            <div className={styles.periodRowField}>
              <Field
                fieldType="date"
                config={{ label: 'Дата початку', type: 'text' }}
                value={draft.startDate}
                changed={(event) => patch({ startDate: event.target.value })}
              />
            </div>
            <div className={styles.periodRowField}>
              <Field
                fieldType="input"
                config={{ label: 'Час початку', type: 'text', mask: 'time', icon: 'clock', iconPosition: 'left' }}
                value={draft.startTime}
                changed={(event) => patch({ startTime: event.target.value })}
              />
            </div>
            {!draft.openEnded ? (
              <>
                <div className={styles.periodRowField}>
                  <Field
                    fieldType="date"
                    config={{ label: 'Дата завершення', type: 'text' }}
                    value={draft.endDate}
                    changed={(event) => patch({ endDate: event.target.value })}
                  />
                </div>
                <div className={styles.periodRowField}>
                  <Field
                    fieldType="input"
                    config={{
                      label: 'Час завершення',
                      type: 'text',
                      mask: 'time',
                      icon: 'clock',
                      iconPosition: 'left',
                    }}
                    value={draft.endTime}
                    changed={(event) => patch({ endTime: event.target.value })}
                  />
                </div>
              </>
            ) : null}
            <div className={`${giftStyles.switchRow} ${styles.periodSwitch}`}>
              <Switch
                checked={draft.openEnded}
                onChange={(next) => patch({ openEnded: next })}
                ariaLabel="Безстроково"
              />
              <span className={giftStyles.switchLabel}>Безстроково</span>
            </div>
          </div>

          {AME_PROMOTION_TYPE === 'promoCode' ? (
            <>
              <div className={styles.promoCodeTypeRow}>
                <div className={styles.titleField}>
                  <Field
                    fieldType="select"
                    config={{
                      label: 'Тип промокоду',
                      getOptions: AME_PROMO_CODE_TYPE_OPTIONS,
                    }}
                    value={AME_PROMO_CODE_TYPE}
                    changed={() => {}}
                  />
                </div>
                {AME_PROMO_CODE_TYPE === 'discount' ? (
                  <>
                    <div className={styles.titleField}>
                      <Field
                        fieldType="select"
                        config={{
                          label: 'Вид знижки',
                          getOptions: AME_DISCOUNT_KIND_OPTIONS,
                        }}
                        value={draft.discountKind}
                        changed={(event) => patch({ discountKind: event.target.value })}
                      />
                    </div>
                    <div className={styles.titleField}>
                      {draft.discountKind === AME_DISCOUNT_KIND.fixed ? (
                        <Field
                          fieldType="input"
                          config={{ label: 'Сума знижки, ₴', type: 'text', mask: 'number' }}
                          value={draft.amount}
                          changed={(event) => patch({ amount: event.target.value })}
                        />
                      ) : (
                        <Field
                          fieldType="input"
                          config={{ label: 'Відсоток знижки', type: 'text', mask: 'number' }}
                          value={draft.percent}
                          changed={(event) => patch({ percent: event.target.value })}
                        />
                      )}
                    </div>
                    <div className={`${giftStyles.switchRow} ${styles.periodSwitch}`}>
                      <Switch
                        checked={draft.applyToSpecPrice}
                        onChange={(next) => patch({ applyToSpecPrice: next })}
                        ariaLabel="Застосовувати до спецціни"
                      />
                      <span className={giftStyles.switchLabel}>Застосовувати знижку до спецціни</span>
                    </div>
                  </>
                ) : null}
              </div>

              <div className={styles.promoCodeTypeRow}>
                <div className={styles.titleField}>
                  <Field
                    fieldType="select"
                    config={{
                      label: 'Тип видачі промокоду',
                      getOptions: AME_ISSUE_KIND_OPTIONS,
                    }}
                    value={draft.issueKind}
                    changed={(event) => patch({ issueKind: event.target.value })}
                    isChangable={isNew}
                  />
                </div>
                {draft.issueKind === AME_ISSUE_KIND_BY_PHRASE ? (
                  <div className={styles.titleField}>
                    <Field
                      fieldType="input"
                      config={{ label: 'Кодова фраза', type: 'text' }}
                      value={draft.phrase}
                      changed={(event) => patch({ phrase: event.target.value })}
                    />
                  </div>
                ) : null}
              </div>

              <div className={styles.textEditor}>
                <Field
                  key={`description-${id || 'new'}`}
                  fieldType="texteditor"
                  config={{ label: 'Опис промокоду', placeholder: '' }}
                  value={descriptionSeed}
                  initialValue={descriptionSeed}
                  changed={(html) => patch({ description: typeof html === 'string' ? html : '' })}
                  isChangable
                />
              </div>

              <AmeCampaignAssortmentFields
                giftStyles={giftStyles}
                scope={draft.scope}
                includeMode={draft.includeMode}
                includeBrands={draft.includeBrands}
                includeCategories={draft.includeCategories}
                includeProducts={draft.includeProducts}
                excludeBrands={draft.excludeBrands}
                excludeCategories={draft.excludeCategories}
                excludeProducts={draft.excludeProducts}
                brands={brands || []}
                brandsLoading={brandsLoading}
                brandsError={brandsError}
                taxonomyNodes={taxonomyNodes}
                searchIndex={searchIndex || []}
                productFilters={productFilters}
                productsLoading={productsLoading}
                productsError={productsError}
                onLoadProducts={loadProducts}
                onChange={patch}
              />
            </>
          ) : null}
        </div>
      </ModulePanel>

      <div className={giftStyles.footer}>
        <Button
          title="Зберегти"
          theme="solid"
          fill="accent"
          size={32}
          isLoading={isSaving}
          disabled={isSaving}
          onClick={handleSave}
        />
        <Button
          title="Скасувати"
          theme="solid"
          fill="normal"
          size={32}
          onClick={() => history.push('/marketing/ame')}
        />
      </div>
    </div>
  )
}

export default AmeCampaignEditor
